/**
 * WCAG contrast auditing for harvested palettes.
 *
 * A palette lifted off someone else's CSS bundle is a set of colours that worked
 * in THEIR layout. Recombined into our card/chip/table vocabulary the pairs shift,
 * and the failure mode is silent: text that is technically painted and practically
 * unreadable. So the renderer measures the pairs it is about to use and says so.
 */
import type { Brand, Rgb } from '@clawharbor/manifest';
import { colorFrom, parseCssColor, flatten, relativeLuminance, shiftL, toHex } from '@clawharbor/manifest';

export type { Rgb };
export { flatten, relativeLuminance };

/**
 * Parse any colour form `tokensToCss` can emit.
 *
 * This used to be a local parser that understood hex and rgb() and nothing else,
 * so every `oklch()` token in a harvested palette was reported `skipped` — an
 * unmeasured pair silently absent from the audit. It now delegates to the single
 * parser in @clawharbor/manifest, which brand-extract already used to READ those
 * same palettes: hex, rgb(), hsl(), oklch(), oklab() and the full 148-entry
 * named table. Anything genuinely unmeasurable (currentColor, transparent,
 * var(), gradients) still returns null, and null is still reported as `skipped`
 * rather than as a pass.
 */
export function parseColor(input: string | undefined): Rgb | null {
  if (!input) return null;
  return parseCssColor(input);
}

/** WCAG 2.x contrast ratio, 1..21. Returns null when either colour is unmeasurable. */
export function contrastRatio(fg: string | undefined, bg: string | undefined): number | null {
  const b = parseColor(bg);
  const fRaw = parseColor(fg);
  if (!b || !fRaw) return null;
  const f = flatten(fRaw, b);
  const lf = relativeLuminance(f);
  const lb = relativeLuminance(b);
  const hi = Math.max(lf, lb);
  const lo = Math.min(lf, lb);
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

/**
 * The tenant's own colour, darkened or lightened only until the pair clears.
 *
 * `bestForeground` answers black or white, and black or white is a repaint
 * rather than a repair: on kino-vela it turned a soft grey-green meta line
 * (#899ea2, 2.81:1) into pure black — more contrast than the body text above it,
 * so the page's own hierarchy inverted, and every harvested border became a hard
 * black rule. Stepping the lightness in OKLCH keeps the hue and the chroma the
 * site chose and stops at the first shade that passes, which is the smallest
 * change that makes the text legible. Falls back to the pole when even the pole
 * of that hue cannot clear the requirement — a foreground and a ground that
 * close leave nothing else to try.
 */
export function towardReadable(fg: string, bg: string, required: number): string {
  const start = colorFrom(fg);
  const ground = colorFrom(bg);
  if (!start || !ground) return bestForeground(bg);

  // Toward whichever pole has room: away from the ground's own lightness.
  const direction = relativeLuminance(flatten(start.rgb, ground.rgb)) >= relativeLuminance(ground.rgb) ? 1 : -1;
  for (let step = 1; step <= 40; step++) {
    const candidate = toHex(shiftL(start, direction * step * 0.025).rgb);
    const ratio = contrastRatio(candidate, bg);
    if (ratio != null && ratio >= required) return candidate;
  }
  return bestForeground(bg);
}

/** Pure black or pure white, whichever is more readable on `bg`. */
export function bestForeground(bg: string | undefined, dark = '#000000', light = '#ffffff'): string {
  const d = contrastRatio(dark, bg);
  const l = contrastRatio(light, bg);
  if (d == null || l == null) return light;
  return d >= l ? dark : light;
}

export interface ContrastIssue {
  /** Palette roles, e.g. 'fg.default' on 'bg.canvas'. */
  fg: string;
  bg: string;
  fgValue?: string;
  bgValue?: string;
  ratio: number | null;
  required: number;
  /** What the pair is used for, so the operator knows what will look broken. */
  usage: string;
  status: 'pass' | 'fail' | 'skipped';
  /** The colour the renderer would substitute if asked to self-heal. */
  suggestion?: string;
}

/**
 * The pairs this stylesheet actually puts on screen. Body text needs 4.5:1;
 * chrome that carries no text meaning (borders) needs 3:1 per WCAG 1.4.11.
 */
const PAIRS: ReadonlyArray<{ fg: string; bg: string; required: number; usage: string }> = [
  { fg: 'fg.default', bg: 'bg.canvas', required: 4.5, usage: 'body text on the page ground' },
  { fg: 'fg.default', bg: 'bg.surface', required: 4.5, usage: 'card title and body text' },
  { fg: 'fg.muted', bg: 'bg.surface', required: 4.5, usage: 'card meta line and freshness chip' },
  { fg: 'fg.muted', bg: 'bg.canvas', required: 4.5, usage: 'disclaimer bar text' },
  { fg: 'brand.onPrimary', bg: 'brand.primary', required: 4.5, usage: 'primary button label' },
  { fg: 'fg.onBrand', bg: 'brand.primary', required: 4.5, usage: 'badge label on the brand chip' },
  // accent.premium carries no text in this stylesheet: it is the disclaimer's
  // left rule and the premium badge's fill. Auditing it at 4.5 measured a use it
  // does not have and pushed a perfectly good accent to black; 1.4.11 is the
  // rule that applies, and the badge's LABEL is audited separately below.
  { fg: 'accent.premium', bg: 'bg.canvas', required: 3, usage: 'disclaimer rule and premium badge fill' },
  { fg: 'fg.onBrand', bg: 'accent.premium', required: 4.5, usage: 'premium badge label' },
  { fg: 'border.default', bg: 'bg.surface', required: 3, usage: 'card and table borders' },
  { fg: 'state.danger', bg: 'bg.surface', required: 4.5, usage: 'sold-out / error text' },
  // The focus ring is drawn in the primary, on the page's own ground. A ring
  // nobody can see against the background it sits on is a keyboard user with no
  // idea where they are, so it is measured like any other non-text indicator.
  { fg: 'brand.primary', bg: 'bg.canvas', required: 3, usage: 'focus ring and card hover border' },
];

/**
 * Measure every pair the base stylesheet uses. Roles the tenant did not define
 * are skipped rather than invented — an absent role falls back to a literal in
 * the CSS and is not this function's business.
 */
export function auditContrast(brand: Brand): ContrastIssue[] {
  const out: ContrastIssue[] = [];
  const palette = brand.palette as Record<string, { value?: string } | undefined>;

  for (const pair of PAIRS) {
    const fgValue = palette[pair.fg]?.value;
    const bgValue = palette[pair.bg]?.value;
    if (!fgValue || !bgValue) continue;

    const ratio = contrastRatio(fgValue, bgValue);
    const status: ContrastIssue['status'] =
      ratio == null ? 'skipped' : ratio >= pair.required ? 'pass' : 'fail';

    const issue: ContrastIssue = {
      fg: pair.fg,
      bg: pair.bg,
      fgValue,
      bgValue,
      ratio,
      required: pair.required,
      usage: pair.usage,
      status,
    };
    if (status === 'fail') issue.suggestion = towardReadable(fgValue, bgValue, pair.required);
    out.push(issue);
  }
  return capMuted(out, palette);
}

/**
 * The muted text may never end up louder than the default text it sits under.
 *
 * A repair measures one pair at a time, and one pair at a time is how the
 * hierarchy inverts: `fg.muted` fails, gets corrected until it clears 4.5, and
 * lands at a higher contrast than `fg.default`, which passed at 5.1 and was
 * therefore never touched. The meta line then shouts over the title. Capping the
 * correction at the default's own value keeps the repair from rewriting the
 * page's typography; the two reading as the same weight is the worst it can do.
 */
function capMuted(issues: ContrastIssue[], palette: Record<string, { value?: string } | undefined>): ContrastIssue[] {
  const defaultValue = palette['fg.default']?.value;
  if (!defaultValue) return issues;
  return issues.map((issue) => {
    if (issue.fg !== 'fg.muted' || !issue.suggestion) return issue;
    const corrected = contrastRatio(issue.suggestion, issue.bgValue);
    const ceiling = contrastRatio(defaultValue, issue.bgValue);
    if (corrected == null || ceiling == null || corrected <= ceiling) return issue;
    return { ...issue, suggestion: defaultValue };
  });
}

/** Just the pairs that fail — what a caller usually wants. */
export function contrastFailures(brand: Brand): ContrastIssue[] {
  return auditContrast(brand).filter((i) => i.status === 'fail');
}

export function describeIssue(i: ContrastIssue): string {
  return `${i.fg} on ${i.bg} is ${i.ratio ?? '?'}:1 (needs ${i.required}:1) — ${i.usage}`;
}

/**
 * Roles a repair may repaint: the ones this stylesheet only ever draws INK with.
 *
 * `brand.primary` and `accent.premium` are fills — the button, the badge, the
 * disclaimer's rule — and a fill is a colour other pairs were measured against.
 * Moving one to pass its own audit silently invalidates the label sitting on it,
 * and it changes the tenant's brand colour on their own page, which is a
 * decision no automatic repair gets to make. They are still audited, and the
 * places that must stay visible against them choose a safe colour instead.
 */
const REPAINTABLE = new Set(['fg.default', 'fg.muted', 'fg.onBrand', 'brand.onPrimary', 'border.default', 'state.danger']);

/**
 * Opt-in self-healing: a CSS block that re-points each failing ink var at the
 * nearest shade of the tenant's own colour that clears its requirement. Off by
 * default, because silently recolouring a brand is a decision an operator should
 * make knowingly — but shipping unreadable text is worse than shipping a
 * corrected shade, so the escape hatch exists.
 */
export function contrastSafeOverrides(brand: Brand, selector = ':root'): string {
  const lines: string[] = [];
  const seen = new Set<string>();
  for (const i of contrastFailures(brand)) {
    if (!i.suggestion || seen.has(i.fg) || !REPAINTABLE.has(i.fg)) continue;
    seen.add(i.fg);
    lines.push(
      `  --ch-${i.fg.replace(/\./g, '-')}: ${i.suggestion}; /* was ${i.fgValue}, ${i.ratio}:1 on ${i.bg} */`,
    );
  }
  return lines.length ? `${selector} {\n${lines.join('\n')}\n}` : '';
}
